/**
 * Dedicated axios instance for the patient portal.
 * Auth is an httpOnly `portal_session` cookie set by the backend on
 * login/signup — never readable by JS, closing the localStorage-XSS gap.
 * `withCredentials` makes the browser attach/accept that cookie. We still
 * keep a lightweight (non-sensitive) cached patient display doc in
 * localStorage so the UI can paint instantly before `/me` resolves.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const PORTAL_PATIENT_KEY = 'pharmxai_portal_patient';
export const PORTAL_MEMBER_KEY = 'dutilyne_portal_active_member';

const portalApi = axios.create({
  baseURL: `${BACKEND_URL}/api/portal`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

/** Family profiles: every request acts for the selected person until switched back. */
export function setActiveMemberHeader(memberId) {
  if (memberId) {
    localStorage.setItem(PORTAL_MEMBER_KEY, memberId);
    portalApi.defaults.headers.common['X-Portal-Member'] = memberId;
  } else {
    localStorage.removeItem(PORTAL_MEMBER_KEY);
    delete portalApi.defaults.headers.common['X-Portal-Member'];
  }
}

export const getActiveMemberId = () => localStorage.getItem(PORTAL_MEMBER_KEY) || '';

setActiveMemberHeader(getActiveMemberId());

portalApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      // Let the auth context decide what to do; just clear the stale cache.
      localStorage.removeItem(PORTAL_PATIENT_KEY);
    }
    return Promise.reject(err);
  }
);

export default portalApi;

// Each mounted destination gets a bound client. In-flight requests cannot pick
// up a newly selected pharmacy's headers while retaining an old form payload.
export function scopedPortalApi(destination) {
  const headers = {};
  if (destination?.pharmacy) headers['X-Portal-Pharmacy'] = destination.pharmacy.tenant_id;
  if (destination?.branch) headers['X-Portal-Branch'] = destination.branch.tenant_id;
  const config = (options = {}) => ({ ...options, headers: { ...options.headers, ...headers } });
  return {
    get: (url, options) => portalApi.get(url, config(options)),
    post: (url, data, options) => portalApi.post(url, data, config(options)),
    put: (url, data, options) => portalApi.put(url, data, config(options)),
    delete: (url, options) => portalApi.delete(url, config(options)),
  };
}
